import * as React from "react";
import {
  TrackerPolicy,
  TrackerPolicyContentStatusOptions,
  TrackerPolicyContentTypeOptions
} from "@models/tracker-policy";
import {Modal, Table, Button, Divider, Avatar, Tooltip} from "antd";
import {FormattedMessage, injectIntl, intlShape} from "react-intl";
import FormattedDateTime from "@components/custom/FormattedDateTime";
import Message from "@constants/message";
import {PaginationDefaultParams} from "../custom/Paginator";
import {NavLink} from "react-router-dom";
import {Icon} from 'antd';
import {SubscriptionStatusOptions} from "../../models/subscription";
import {PaymentGatewayOptions} from "../../models/paymentGateway";
import {getFormattedPrice} from "../../utils";
import __i18n from "../../intl/__i18n";

type Props = {
  data: TrackerPolicy[];
  loading: boolean;
  deleteFn: (item: TrackerPolicy) => void;
  intl: intlShape.isRequired;
  currentPage: number;
};

const TrackerPoliciesTable: React.FC<Props> = props => {
  const {data, loading, currentPage, deleteFn, intl} = props;
  const {formatMessage} = intl;
  const pageOffset = (currentPage - 1) * PaginationDefaultParams.count;
  
  const deleteTrackerPolicy = (item: TrackerPolicy): void => {
    Modal.confirm({
      content: formatMessage({
        id: "app.general_delete_warning",
        defaultMessage: Message.Warning.Delete
      }),
      okText: formatMessage({
        id: "app.delete",
        defaultMessage: "Delete"
      }),
      cancelText: formatMessage({
        id: "app.cancel",
        defaultMessage: "Cancel"
      }),
      className: "delete-confirm-dialog",
      onOk: () => deleteFn(item)
    });
  };
  
  const columns = [
    {
      title: <FormattedMessage id="app.index" defaultMessage={`Index`}/>,
      width: 30,
      dataIndex: "id",
      key: "id",
      align: "right",
      render: (a, b, i) => <span>{i + 1 + pageOffset}</span>
    },
    {
      title: __i18n("Mobile Number"),
      dataIndex: "user_identifier",
      key: "user_identifier",
      align: "right",
    },
    {
      title: <FormattedMessage id="app.status" defaultMessage={`Status`}/>,
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status, record) => SubscriptionStatusOptions.find(item => item.value === status)["label"]
    },
    {
      title: <FormattedMessage id="app.price" defaultMessage={`Price`}/>,
      dataIndex: "price",
      key: "price",
      render: (price, record) => getFormattedPrice(price)
    },
    {
      title: <FormattedMessage id="app.effective_price" defaultMessage={`Effective Price`}/>,
      dataIndex: "effective_price",
      key: "effective_price",
      align: "center",
      render: (effective_price, record) => getFormattedPrice(effective_price)
    },
    {
      title: <FormattedMessage id="app.payment_gateway" defaultMessage={`payment_gateway`}/>,
      dataIndex: "payment_gateway",
      key: "payment_gateway",
      align: "center",
      render: (payment_gateway, record) => PaymentGatewayOptions.find(item => item.value === payment_gateway) ? PaymentGatewayOptions.find(item => item.value === payment_gateway)["label"] : payment_gateway
    },
    {
      title: (<FormattedMessage id="app.start_time" defaultMessage={`Start Time`}/>),
      dataIndex: "start_time",
      key: "start_time",
      align: "center",
      render: (start_time, record) => <FormattedDateTime showTime={true} date={start_time}/>
    },
    {
      title: (<FormattedMessage id="app.end_time" defaultMessage={`End Time`}/>),
      dataIndex: "end_time",
      key: "end_time",
      align: "center",
      render: (end_time, record) => <FormattedDateTime showTime={true} date={end_time}/>
    },
    {
      title: "",
      align: "left",
      render: (text, record) => (
        <NavLink to={`/subscriptions/${record.id}/edit`}>
          <Button type="link" size={`small`}>
            <Icon type="edit"/>
          </Button>
        </NavLink>
      )
    }
  ];
  
  return (
    <Table
      columns={columns}
      loading={loading}
      pagination={false}
      dataSource={data}
      rowKey="id"
    />
  );
};

export default injectIntl(TrackerPoliciesTable);
